import React from 'react';
import { ChevronLeft, FileText, Shield, RotateCcw, Mail } from 'lucide-react';

interface LegalPageProps {
  onBack: () => void;
  type: 'privacy' | 'terms' | 'refund';
}

export const LegalPage: React.FC<LegalPageProps> = ({ onBack, type }) => {
  const CONTACT_EMAIL = 'vitacore.fit.wellness@gmail.com';

  const getTitle = () => {
    switch (type) {
      case 'privacy': return 'Privacy Policy';
      case 'terms': return 'Terms and Conditions';
      case 'refund': return 'Refund Policy';
      default: return 'Legal';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'privacy': return <Shield size={24} />;
      case 'terms': return <FileText size={24} />;
      case 'refund': return <RotateCcw size={24} />;
      default: return <FileText size={24} />;
    }
  };

  const getContent = () => {
    switch (type) {
      case 'privacy':
        return (
          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">1. Introduction</h2>
              <p className="text-slate-300 leading-relaxed">
                VitaCore ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">2. Information We Collect</h2>
              <p className="text-slate-300 leading-relaxed mb-3">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Account information (name, email address, profile picture)</li>
                <li>Health and fitness data (workout logs, body metrics, dietary information)</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Usage data and preferences</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">3. How We Use Your Information</h2>
              <p className="text-slate-300 leading-relaxed">
                We use the information we collect to provide, maintain, and improve our services, process payments, send you notifications, and personalize your experience.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">4. Data Security</h2>
              <p className="text-slate-300 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">5. Your Rights</h2>
              <p className="text-slate-300 leading-relaxed">
                You have the right to access, update, or delete your personal information at any time through your account settings or by contacting us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:text-emerald-300 underline">
                  {CONTACT_EMAIL}
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">6. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                If you have questions about this Privacy Policy, please contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:text-emerald-300 underline">
                  {CONTACT_EMAIL}
                </a>.
              </p>
            </section>

            <section className="pt-4 border-t border-slate-800">
              <p className="text-slate-500 text-sm">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </section>
          </div>
        );

      case 'terms':
        return (
          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">1. Acceptance of Terms</h2>
              <p className="text-slate-300 leading-relaxed">
                By accessing and using VitaCore, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">2. Use License</h2>
              <p className="text-slate-300 leading-relaxed mb-3">
                Permission is granted to use VitaCore for personal, non-commercial purposes. This license does not include:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Commercial use or resale of the service</li>
                <li>Modification or reverse engineering of the application</li>
                <li>Use of automated systems to access the service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">3. Subscription Terms</h2>
              <p className="text-slate-300 leading-relaxed">
                Subscriptions to PRO and UNLIMITED plans are billed monthly. Subscriptions automatically renew unless cancelled at least 24 hours before the renewal date. You can cancel your subscription at any time through your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">4. User Responsibilities</h2>
              <p className="text-slate-300 leading-relaxed">
                You are responsible for maintaining the confidentiality of your account and password. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">5. Limitation of Liability</h2>
              <p className="text-slate-300 leading-relaxed">
                VitaCore provides fitness and health information for informational purposes only. We are not medical professionals, and our services should not replace professional medical advice, diagnosis, or treatment.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">6. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                For questions about these Terms, please contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:text-emerald-300 underline">
                  {CONTACT_EMAIL}
                </a>.
              </p>
            </section>

            <section className="pt-4 border-t border-slate-800">
              <p className="text-slate-500 text-sm">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </section>
          </div>
        );

      case 'refund':
        return (
          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">1. Refund Policy</h2>
              <p className="text-slate-300 leading-relaxed">
                VitaCore offers subscription plans (PRO and UNLIMITED) on a monthly basis. All subscriptions are processed securely through Stripe.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">2. Refund Eligibility</h2>
              <p className="text-slate-300 leading-relaxed mb-3">
                You may be eligible for a refund in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Refund requested within 14 days of initial subscription purchase</li>
                <li>Technical issues preventing access to paid features</li>
                <li>Duplicate charges or billing errors</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">3. How to Request a Refund</h2>
              <p className="text-slate-300 leading-relaxed">
                To request a refund, please contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:text-emerald-300 underline">
                  {CONTACT_EMAIL}
                </a>{' '}
                with your account email and reason for the refund request. We will review your request and respond within 5-7 business days.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">4. Processing Time</h2>
              <p className="text-slate-300 leading-relaxed">
                Approved refunds will be processed to your original payment method within 5-10 business days. The refunded amount will appear on your statement within 1-2 billing cycles.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">5. Non-Refundable Items</h2>
              <p className="text-slate-300 leading-relaxed">
                The following are not eligible for refunds: purchases made more than 14 days ago, cancelled subscriptions after the billing period has started, and items purchased through the VitaCore Shop (subject to separate return policy).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">6. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                For refund inquiries, please contact us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-400 hover:text-emerald-300 underline">
                  {CONTACT_EMAIL}
                </a>.
              </p>
            </section>

            <section className="pt-4 border-t border-slate-800">
              <p className="text-slate-500 text-sm">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </section>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-4xl mx-auto animate-fadeIn">
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={onBack} 
          className="p-3 bg-slate-900 border border-slate-800 rounded-2xl hover:bg-slate-800 transition-colors"
        >
          <ChevronLeft size={24} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 rounded-2xl text-emerald-400">
            {getIcon()}
          </div>
          <div>
            <h1 className="text-2xl font-black italic tracking-tighter uppercase text-white">{getTitle()}</h1>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Legal Information</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 md:p-12 shadow-xl">
        {getContent()}
      </div>
    </div>
  );
};

